import { FC, useEffect, useState } from 'react';

import { useI18n } from '#hooks/useI18n';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Check, Cross, Flex, Info, Regular, Semibold, Warning } from '@zazume/zzm-base';
import { IBAN } from '@zazume/zzm-utils';

import { Can } from '#/components/roles/Can';
import { useGetVoPConsentStatus } from '#/hooks/api/vop/useGetVoPConsentStatus';
import { useVoPAPI } from '#/hooks/api/vop/useVoPAPI';
import { VoPValidationResult } from '#lib/network/api/vop/vop';

const ValidateLink = styled.span<{ disabled?: boolean }>(
  ({ theme, disabled }) => css`
    font-family: ${theme.fonts.base};
    font-weight: ${theme.fontWeights.semibold};
    font-size: ${theme.fontsSizes.xs};
    line-height: ${theme.lineHeights.s};
    color: ${disabled ? theme.colors.PrimaryLight2 : theme.colors.Primary};
    cursor: ${disabled ? 'default' : 'pointer'};

    &:hover {
      color: ${disabled ? theme.colors.PrimaryLight2 : theme.colors.PrimaryDark2};
    }
  `
);

interface IbanVoPValidationProps {
  iban?: string;
  name?: string;
}

const IbanVoPValidationContent: FC<IbanVoPValidationProps> = ({ iban, name }) => {
  const { t } = useI18n();
  const { consentStatus } = useGetVoPConsentStatus();
  const { validateIban, isValidating } = useVoPAPI();
  const [result, setResult] = useState<VoPValidationResult | null>(null);

  const isConnected = consentStatus?.connected;

  useEffect(() => {
    setResult(null);
  }, [iban, name]);

  if (!isConnected) {
    return null;
  }

  const isIbanValid = Boolean(iban && IBAN.isValid(iban));
  const canValidate = isIbanValid && Boolean(name);

  const suggestedName = result?.suggestedName;

  const handleValidate = async () => {
    if (!canValidate || isValidating) {
      return;
    }
    const response = await validateIban(iban!, name!);
    if (response) {
      setResult(response);
    }
  };

  if (!result) {
    return (
      <ValidateLink
        disabled={!canValidate || isValidating}
        onClick={canValidate && !isValidating ? handleValidate : undefined}
      >
        {t('settings.vop.validateIban')}
      </ValidateLink>
    );
  }

  if (result.resultCode === 'matched') {
    return (
      <Flex gap="s4" align="center">
        <Check size={16} color="GreenDark1" />
        <Semibold variant="S" color="GreenDark1">
          {t('settings.vop.validation.matched')}
        </Semibold>
      </Flex>
    );
  }

  if (result.resultCode === 'close_match') {
    return (
      <Flex direction="column" gap="s2">
        <Flex gap="s4" align="center">
          <Warning size={16} color="MostardDark" />
          <Semibold variant="S" color="MostardDark">
            {t('settings.vop.validation.partiallyMatches')}
          </Semibold>
        </Flex>
        {suggestedName && (
          <Regular variant="S" color="MostardDark">
            {t('settings.vop.validation.closeMatchSuggestedName', { name: suggestedName })}
          </Regular>
        )}
      </Flex>
    );
  }

  if (result.resultCode === 'not_matched') {
    return (
      <Flex direction="column" gap="s2">
        <Flex gap="s4" align="center">
          <Cross size={16} color="RedDark1" />
          <Semibold variant="S" color="RedDark1">
            {t('settings.vop.validation.notMatched')}
          </Semibold>
        </Flex>
        <Regular variant="S" color="RedDark1">
          {t('settings.vop.validation.notMatchedHint')}
        </Regular>
      </Flex>
    );
  }

  return (
    <Flex gap="s4" align="center">
      <Info size={16} color="Gray500" />
      <Regular variant="S" color="Gray500">
        {t(
          result.resultCode === 'cannot_be_checked'
            ? 'settings.vop.validation.cannotBeChecked'
            : 'settings.vop.validation.temporarilyUnavailable'
        )}
      </Regular>
    </Flex>
  );
};

export const IbanVoPValidation: FC<IbanVoPValidationProps> = props => {
  return (
    <Can hasRoles={['admin', 'buildingManager']}>
      <IbanVoPValidationContent {...props} />
    </Can>
  );
};
